// Utility functions for handling recurring tasks
//
// Carla's decision 2 (2026-10-12): per-occurrence exceptions. A series row carries
// recurrenceExceptions, one entry per occurrence named by its own start (a wall clock):
//   { occurrenceStart, skip }        deleted on its own: not drawn
//   { occurrenceStart, completed }   ticked done on its own: that occurrence alone is completed
//   { occurrenceStart, override }    changed on its own: drawn where and as the override says
// The server works the occurrences out by the same rule (Aidey Backend/lib/taskSeries.js); both
// assert Aidey Backend/ci/task_occurrence_cases.json.

const pad2 = (n) => String(n).padStart(2, '0');
// A local Date as the wall clock tasks are stored in ("2026-10-19T09:00:00").
function wallOf(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
}
// A stored value as that wall clock, or null.
function wallKey(value) {
  if (!value) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?$/.exec(String(value).trim().slice(0, 19));
  return m ? `${m[1]}-${m[2]}-${m[3]}T${m[4] || '00'}:${m[5] || '00'}:${m[6] || '00'}` : null;
}

export function exceptionsByStart(task) {
  const list = Array.isArray(task?.recurrenceExceptions) ? task.recurrenceExceptions : [];
  const map = new Map();
  list.forEach((e) => { const k = wallKey(e?.occurrenceStart); if (k) map.set(k, { ...e, occurrenceStart: k }); });
  return map;
}

function isValidDate(value) {
  if (!(value instanceof Date)) return false;
  return !Number.isNaN(value.getTime());
}

function normalizeRecurrenceType(recurrence) {
  if (!recurrence) return null;
  const raw = recurrence.type || recurrence.frequency || recurrence.rule;
  return typeof raw === 'string' ? raw.toLowerCase() : null;
}

export function expandRecurringTasks(tasks, startDate, endDate) {
  const expandedTasks = [];

  const rangeStart = new Date(startDate);
  const rangeEnd = new Date(endDate);
  if (!isValidDate(rangeStart) || !isValidDate(rangeEnd) || rangeStart > rangeEnd) {
    return tasks || [];
  }

  (tasks || []).forEach(task => {
    if (!task?.recurrence) {
      expandedTasks.push(task);
      return;
    }

    const instances = generateRecurringInstances(task, rangeStart, rangeEnd);
    expandedTasks.push(...instances);
  });

  return expandedTasks;
}

function generateRecurringInstances(baseTask, startDate, endDate) {
  const instances = [];
  const recurrence = baseTask?.recurrence;

  if (!recurrence) return [baseTask];

  const type = normalizeRecurrenceType(recurrence);
  if (!['daily', 'weekly', 'monthly', 'weekday', 'yearly', 'annually'].includes(type)) {
    return [baseTask];
  }

  const taskStart = new Date(baseTask.datetime);
  const taskEndRaw = baseTask.deadline ? new Date(baseTask.deadline) : null;

  if (!isValidDate(taskStart)) {
    return [baseTask];
  }

  const safeTaskEnd = isValidDate(taskEndRaw) ? taskEndRaw : new Date(taskStart.getTime() + 60 * 60 * 1000);
  const duration = Math.max(0, safeTaskEnd.getTime() - taskStart.getTime());

  const interval = Number.isFinite(Number(recurrence.interval)) && Number(recurrence.interval) > 0
    ? Number(recurrence.interval)
    : 1;
  const maxCount = Number.isFinite(Number(recurrence.count)) && Number(recurrence.count) > 0
    ? Number(recurrence.count)
    : null;
  const untilDate = recurrence.until ? new Date(recurrence.until) : null;

  let current = new Date(taskStart);
  let generatedCount = 0;
  const hardLimit = 800;
  const exceptions = exceptionsByStart(baseTask);

  while (current <= endDate && generatedCount < hardLimit) {
    if (isValidDate(untilDate) && current > untilDate) break;
    if (maxCount !== null && generatedCount >= maxCount) break;

    // Decision 2: the occurrence's own name, and what was said about it on its own.
    const occurrenceStart = wallOf(current);
    const exception = exceptions.get(occurrenceStart) || {};
    const override = exception.override || {};
    const overStart = override.datetime ? new Date(override.datetime) : null;
    const overEnd = override.deadline ? new Date(override.deadline) : null;
    const instanceStart = isValidDate(overStart) ? overStart : new Date(current);
    const instanceEnd = isValidDate(overEnd) ? overEnd : new Date(instanceStart.getTime() + duration);

    // A skipped one is not drawn (it still counts towards a series that ends after a number of times).
    if (!exception.skip && instanceStart >= startDate && instanceStart <= endDate) {
      instances.push({
        ...baseTask,
        ...override,
        id: `${baseTask.id}-${current.getTime()}`,
        datetime: instanceStart.toISOString(),
        deadline: instanceEnd.toISOString(),
        date: instanceStart.toISOString().slice(0, 10),
        time: instanceStart.toTimeString().slice(0, 5),
        completed: !!baseTask.completed || !!exception.completed,
        isRecurringInstance: true,
        parentTaskId: baseTask.id,
        // Its place in the series (0 is the first). A series that ends after a count is shared at
        // this place when one is changed "this and following" (utils/recurringEdit.js).
        occurrenceIndex: generatedCount,
        occurrenceStart,
      });
    }

    generatedCount += 1;

    if (type === 'daily') {
      current.setDate(current.getDate() + interval);
      continue;
    }

    if (type === 'weekday') {
      // Advance to next Mon-Fri
      current.setDate(current.getDate() + 1);
      while (current.getDay() === 0 || current.getDay() === 6) {
        current.setDate(current.getDate() + 1);
      }
      continue;
    }

    if (type === 'yearly' || type === 'annually') {
      current.setFullYear(current.getFullYear() + interval);
      continue;
    }

    if (type === 'weekly') {
      const fallbackDay = taskStart.getDay();
      const targetDay = recurrence.day ? getDayNumber(recurrence.day) : fallbackDay;
      current = getNextWeekday(current, targetDay, interval);
      continue;
    }

    if (type === 'monthly') {
      const targetDate = Number.isFinite(Number(recurrence.date))
        ? Number(recurrence.date)
        : taskStart.getDate();
      current = getNextMonthlyDate(current, targetDate, interval);
      continue;
    }
  }

  return instances;
}

function getDayNumber(dayName) {
  const days = {
    sunday: 0, monday: 1, tuesday: 2, wednesday: 3,
    thursday: 4, friday: 5, saturday: 6
  };
  return days[dayName.toLowerCase()] || 0;
}

function getNextWeekday(fromDate, targetDay, intervalWeeks = 1) {
  const result = new Date(fromDate);
  const currentDay = result.getDay();
  let daysToAdd = targetDay - currentDay;

  if (daysToAdd <= 0) {
    daysToAdd += 7 * intervalWeeks;
  } else {
    daysToAdd += 7 * (intervalWeeks - 1);
  }

  result.setDate(result.getDate() + daysToAdd);
  return result;
}

function getNextMonthlyDate(fromDate, targetDate, intervalMonths = 1) {
  const result = new Date(fromDate);
  result.setMonth(result.getMonth() + intervalMonths);

  // Handle cases where the target date doesn't exist in the month (e.g., 31st)
  const lastDayOfMonth = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();
  const actualDate = Math.min(targetDate, lastDayOfMonth);

  result.setDate(actualDate);
  return result;
}

export function getRecurringTasksForDateRange(tasks, startDate, endDate) {
  return expandRecurringTasks(tasks, startDate, endDate);
}

export function getRecurringTasksForDate(tasks, date) {
  const dayStart = new Date(date);
  dayStart.setHours(0, 0, 0, 0);

  const dayEnd = new Date(date);
  dayEnd.setHours(23, 59, 59, 999);

  return expandRecurringTasks(tasks, dayStart, dayEnd);
}
